import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import CustomStore from 'devextreme/data/custom_store';
import { useEffect, useMemo, useRef } from 'react';
import type { DirectoryUser } from '../../../../api/timeOff';
import { searchEnabledDirectoryUsers } from '../../../../api/timeOff';

interface EmployeeUserPickerProps {
  upn: string;
  displayName: string;
  onChange: (employee: { upn: string; displayName: string }) => void;
}

function userLabel(user: DirectoryUser | null | undefined): string {
  if (!user) return '';
  const email = user.mail?.trim();
  if (email && email.toLowerCase() !== user.displayName.trim().toLowerCase()) {
    return `${user.displayName} — ${email}`;
  }
  return user.displayName;
}

function cacheUser(cache: Map<string, DirectoryUser>, user: DirectoryUser) {
  cache.set(user.id, user);
  const principal = user.userPrincipalName ?? user.mail;
  if (principal) cache.set(principal.toLowerCase(), user);
}

export function EmployeeUserPicker({ upn, displayName, onChange }: EmployeeUserPickerProps) {
  const userCacheRef = useRef(new Map<string, DirectoryUser>());

  useEffect(() => {
    const trimmedUpn = upn.trim();
    if (!trimmedUpn) return;
    const existing = userCacheRef.current.get(trimmedUpn.toLowerCase());
    if (existing) return;
    userCacheRef.current.set(trimmedUpn.toLowerCase(), {
      id: trimmedUpn,
      displayName: displayName.trim() || trimmedUpn,
      userPrincipalName: trimmedUpn,
      mail: trimmedUpn
    });
  }, [displayName, upn]);

  const userStore = useMemo(
    () =>
      new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
          const q = String(loadOptions.searchValue ?? '').trim();
          if (q.length < 2) return [];
          try {
            const results = await searchEnabledDirectoryUsers(q);
            for (const user of results) cacheUser(userCacheRef.current, user);
            return results;
          } catch {
            return [];
          }
        },
        byKey: async (key) => {
          const cached = userCacheRef.current.get(String(key));
          if (cached) return cached;
          const byUpn = userCacheRef.current.get(String(key).toLowerCase());
          if (byUpn) return byUpn;
          const text = String(key);
          return {
            id: text,
            displayName: displayName.trim() || text,
            userPrincipalName: text,
            mail: text
          };
        }
      }),
    [displayName]
  );

  const selectedId = (() => {
    const trimmedUpn = upn.trim();
    if (!trimmedUpn) return null;
    return userCacheRef.current.get(trimmedUpn.toLowerCase())?.id ?? null;
  })();

  return (
    <div className="employee-user-picker">
      <div>
        <label className="employee-user-picker__label">Choose Employee</label>
        <SelectBox
          dataSource={userStore}
          displayExpr={userLabel}
          valueExpr="id"
          searchEnabled
          searchMode="contains"
          searchExpr={['displayName', 'mail', 'userPrincipalName']}
          minSearchLength={2}
          searchTimeout={350}
          showDataBeforeSearch={false}
          acceptCustomValue
          showClearButton
          width="100%"
          placeholder="Search for a user"
          noDataText="No matches found"
          dropDownOptions={{ maxHeight: 280 }}
          value={selectedId}
          onCustomItemCreating={(e) => {
            const text = String(e.text ?? '').trim();
            if (!text) {
              e.customItem = undefined;
              return;
            }
            e.customItem = {
              id: text,
              displayName: '',
              userPrincipalName: text,
              mail: text
            };
          }}
          onValueChanged={(e) => {
            const id = e.value as string | null | undefined;
            if (!id) {
              onChange({ upn: '', displayName: '' });
              return;
            }
            const selected =
              userCacheRef.current.get(id) ?? userCacheRef.current.get(id.toLowerCase());
            if (selected) {
              const selectedUpn = selected.userPrincipalName ?? selected.mail ?? id;
              cacheUser(userCacheRef.current, selected);
              onChange({ upn: selectedUpn, displayName: selected.displayName });
              return;
            }
            onChange({ upn: id, displayName: '' });
          }}
        />
      </div>
      <div>
        <label className="employee-user-picker__label">Selected Employee</label>
        <TextBox value={displayName} readOnly width="100%" />
      </div>
    </div>
  );
}
